import { Job, JobStatus, SupabaseJob } from '@applicator/shared'
import { useState, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'


import { loadRelevanceSettings } from '../../lib/relevanceSettings'
import { JOBS_TABLE_COMPACT_COLUMN_KEYS, useCompactViewport } from '../../hooks/useCompactViewport'
import DatePicker from '../DatePicker/DatePicker'
import { Table, TableColumn } from '../Table'

import { createTableColumns } from './columns'
import { createLoadJobs } from './data'
import { useJobsTableEffects } from './effects'
import { createHandlers } from './handlers'
import { createHelpers } from './helpers'
import { DeleteConfirmDialog, ReviewRulesDialog } from './index/dialogs'
import { useRelevanceCountsEffect, useLazyLoadingResetEffect, useErrorToastEffect } from './index/effects'
import { filterJobs } from './index/filtering'
import { handleSort, handlePrepareClick } from './index/handlers'
import { LoadingState } from './index/loadingStates'
import { calculateRelevanceCounts } from './index/relevanceCounts'
import { getEmptyMessage } from './index/tableConfig'
import { renderToolbar } from './index/toolbar'
import { sortJobs } from './sorting'
import { useTableHandlers } from './table'
import { JobsTableProps } from './types'

function JobsTable({ activeStatus, searchQuery, searchQueryId, relevanceFilter = 'all', onStatusChange: _onStatusChange, onStatusCountsChange, onRelevanceCountsChange, onAddJobRef, onBulkDeleteRef, onBulkAddToJobsRef, onSelectedCountChange, onRefreshRef }: JobsTableProps) {
  const navigate = useNavigate()
  const isCompactViewport = useCompactViewport()
  const jobsTableVisibleColumnKeys = isCompactViewport ? [...JOBS_TABLE_COMPACT_COLUMN_KEYS] : undefined
  const [reviewRulesJob, setReviewRulesJob] = useState<{ job: Job; supabaseJob: SupabaseJob } | null>(null)


  const [jobs, setJobs] = useState<Job[]>([])
  const [supabaseJobs, setSupabaseJobs] = useState<SupabaseJob[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [, setDisplayedJobsCount] = useState(20) // Lazy loading: show first 20
  const [allJobsLoaded, setAllJobsLoaded] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean
    type: 'single' | 'bulk'
    message: string
    jobIds: string[]
    rowIndex?: number
  } | null>(null)
  const [analyzingJobIds, setAnalyzingJobIds] = useState<Set<string>>(new Set())
  const [relevanceSettings, setRelevanceSettings] = useState<Awaited<ReturnType<typeof loadRelevanceSettings>> | null>(null)
  const [userRules, setUserRules] = useState<{ follow_up_days_after_applied?: number | null; deadline_days_after_saved?: number | null }>({
    follow_up_days_after_applied: 7,
    deadline_days_after_saved: 14,
  })
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Create loadJobs function
  const loadJobs = useMemo(() => createLoadJobs({
    searchQueryId,
    userRules,
    setJobs,
    setSupabaseJobs,
    setLoading,
    setError,
    setDisplayedJobsCount,
    setAllJobsLoaded,
  }), [searchQueryId, userRules, setJobs, setSupabaseJobs, setLoading, setError, setDisplayedJobsCount, setAllJobsLoaded])

  // Create handlers
  const handlers = useMemo(() => createHandlers({
    jobs,
    supabaseJobs,
    setJobs,
    setSupabaseJobs,
    setError,
    setAnalyzingJobIds,
    analyzingJobIds,
    relevanceSettings,
    setRelevanceSettings,
    userRules,
    loadJobs,
  }), [jobs, supabaseJobs, analyzingJobIds, relevanceSettings, userRules, loadJobs])

  const handleStatusChange = handlers.handleStatusChange
  const handleAnalyzeRelevance = handlers.handleAnalyzeRelevance

  // Create helpers (depends on handleAnalyzeRelevance from handlers)
  const helpers = useMemo(() => createHelpers({
    jobs,
    supabaseJobs,
    selectedRows,
    setJobs,
    setSupabaseJobs,
    setSelectedRows,
    setError,
    setDeleteConfirm,
    deleteConfirm,
    userRules,
    handleAnalyzeRelevance,
    searchQueryId,
    loadJobs,
  }), [jobs, supabaseJobs, selectedRows, deleteConfirm, userRules, handleAnalyzeRelevance, searchQueryId, loadJobs])

  const handleAddJob = helpers.handleAddJob
  const handleBulkDelete = helpers.handleBulkDelete
  const confirmBulkDelete = helpers.confirmBulkDelete
  const confirmDeleteJob = helpers.confirmDeleteJob
  const handleBulkAnalyzeRelevance = helpers.handleBulkAnalyzeRelevance
  const handleAddToJobs = helpers.handleAddToJobs
  const handleRejectRelevant = helpers.handleRejectRelevant
  const handleBulkAddToJobs = helpers.handleBulkAddToJobs

  // Use effects hook
  useJobsTableEffects({
    searchQueryId,
    loadJobs,
    onRefreshRef,
    onAddJobRef,
    onBulkDeleteRef,
    onBulkAddToJobsRef,
    onSelectedCountChange,
    onStatusCountsChange,
    jobs,
    supabaseJobs,
    userRules,
    selectedRows,
    handleAddJob,
    handleBulkDelete,
    handleBulkAddToJobs,
    setJobs,
    setSupabaseJobs,
    setUserRules,
    setRelevanceSettings,
  })

  const statusOptions: JobStatus[] = [
    'Bookmarked',
    'Applying',
    'Applied',
    'Interviewing',
    'Negotiating',
    'Accepted',
    'I Withdrew',
    'Not Selected',
    'No Response',
    'Archived',
  ]



  // Подсчет релевантности для статистики
  // Используем relevance_verdict для синхронизации с SearchQueries
  const relevanceCounts = useMemo(() => {
    return calculateRelevanceCounts(jobs, supabaseJobs)
  }, [jobs, supabaseJobs])

  // Фильтрация вакансий по активному статусу, поисковому запросу и релевантности
  const filteredJobs = useMemo(() => {
    return filterJobs(jobs, activeStatus, searchQuery, relevanceFilter, supabaseJobs)
  }, [jobs, activeStatus, searchQuery, relevanceFilter, supabaseJobs])

  const sortedFilteredJobs = useMemo(() => {
    return sortJobs(filteredJobs, sortColumn, sortDirection, supabaseJobs)
  }, [filteredJobs, sortColumn, sortDirection, supabaseJobs])

  // Передача relevance counts обратно в MainContent
  useRelevanceCountsEffect(relevanceCounts, onRelevanceCountsChange)

  // Сброс lazy loading при изменении фильтров
  useLazyLoadingResetEffect(activeStatus, searchQuery, relevanceFilter, sortedFilteredJobs.length, setDisplayedJobsCount, setAllJobsLoaded)

  // Show error in toast
  useErrorToastEffect(error)

  const handleSortWrapper = (column: string) => {
    handleSort(column, sortColumn, sortDirection, setSortColumn, setSortDirection)
  }

  const handleDeleteJob = (rowIndex: number) => {
    const deleteData = handlers.handleDeleteJob(rowIndex)
    if (deleteData) {
      setDeleteConfirm({
        isOpen: true,
        type: 'single',
        ...deleteData,
      })
    }
  }


  const handleDateChange = handlers.handleDateChange
  const handleQuickAction = handlers.handleQuickAction

  // DatePicker positioning and inline editing handlers are not needed - Table component handles these via datePicker and editable props

  const handleExcitementChange = handlers.handleExcitementChange

  // renderStars is not used (Table component handles rating UI)

  // getStatusDisplay is imported from utils.ts

  // Define columns for Table component
  const tableColumns: TableColumn<Job>[] = useMemo(() => {
    const cols = createTableColumns({
      jobs,
      supabaseJobs,
      analyzingJobIds,
      statusOptions,
      searchQueryId,
      setJobs,
      setError,
      handleStatusChange,
      handleDateChange,
      handleQuickAction,
      handleExcitementChange,
      handleAnalyzeRelevance,
      handleDeleteJob,
      handleAddToJobs,
      handleRejectRelevant,
    })
    return cols || []
  }, [jobs, supabaseJobs, analyzingJobIds, statusOptions, searchQueryId, handleStatusChange, handleDateChange, handleQuickAction, handleExcitementChange, handleAnalyzeRelevance, handleDeleteJob, handleAddToJobs, handleRejectRelevant])

  // Use table handlers hook
  const {
    tableSelectedRows,
    handleTableRowSelect,
    handleTableSort,
    handleTableRowClick,
    getRowClassName,
    handleLoadMore,
  } = useTableHandlers({
    jobs,
    sortedFilteredJobs,
    selectedRows,
    supabaseJobs,
    searchQueryId,
    sortColumn,
    sortDirection,
    tableColumns,
    handleSort: handleSortWrapper,
    allJobsLoaded,
    isLoadingMore,
    setSelectedRows,
    setDisplayedJobsCount,
    setAllJobsLoaded,
    setIsLoadingMore,
  })

  const loadingState = LoadingState({
    loading,
    error,
    onRetry: () => {
      setError(null)
      loadJobs()
    }
  })
  if (loadingState) return loadingState

  const handlePrepareClickWrapper = (jobIds: string[]) => {
    handlePrepareClick(jobIds, navigate)
  }

  return (
    <div ref={tableWrapperRef} className="h-full flex flex-col border border-border-default rounded-lg" style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Table<Job>
        columns={tableColumns}
        data={sortedFilteredJobs}
        loading={loading}
        error={error}
        onRowClick={handleTableRowClick}
        onRowSelect={handleTableRowSelect}
        selectedRows={tableSelectedRows}
        selectable={true}
        sortable={true}
        sortColumn={sortColumn ? tableColumns.find(col => {
          const columnNameMap: Record<string, string> = {
            'position': 'Job Position',
            'company': 'Company',
            'salary': 'Salary',
            'location': 'Location',
            'status': 'Status',
            'dateSaved': 'Date Saved',
            'dateApplied': 'Date Applied',
            'followUp': 'Follow up',
            'deadline': 'Deadline',
            'excitement': 'Excitement',
          }
          return columnNameMap[col.key] === sortColumn
        })?.key || null : null}
        sortDirection={sortDirection}
        onSort={handleTableSort}
        showColumnManagement={true}
        initialVisibleColumnKeys={jobsTableVisibleColumnKeys}
        emptyMessage={getEmptyMessage(searchQuery, sortedFilteredJobs.length, jobs.length)}
        rowClassName={getRowClassName}
        renderToolbar={(selectedRows, selectedData) => {
          return renderToolbar({
            selectedRows,
            selectedData,
            supabaseJobs,
            relevanceFilter,
            analyzingJobIds,
            handleBulkAnalyzeRelevance,
            handlePrepareClickWrapper,
            setReviewRulesJob,
            loadJobs,
          })
        }}
        lazyLoading={{
          enabled: true,
          initialCount: 20,
          loadMoreThreshold: 0.7,
          onLoadMore: handleLoadMore,
          isLoadingMore: isLoadingMore,
        }}
        datePickerComponent={DatePicker}
        getRowId={(row, index) => row.id || index.toString()}
      />

      <DeleteConfirmDialog
        deleteConfirm={deleteConfirm}
        onConfirm={async () => {
          if (deleteConfirm?.type === 'single') {
            console.log('[JobsTable] Calling confirmDeleteJob for single job')
            await confirmDeleteJob()
          } else if (deleteConfirm?.type === 'bulk') {
            console.log('[JobsTable] Calling confirmBulkDelete for bulk delete')
            await confirmBulkDelete()
          } else {
            console.warn('[JobsTable] Unknown delete type or missing deleteConfirm', deleteConfirm)
          }
        }}
        onCancel={() => setDeleteConfirm(null)}
        setError={setError}
      />

      <ReviewRulesDialog
        reviewRulesJob={reviewRulesJob}
        onClose={() => {
          setReviewRulesJob(null)
          loadJobs()
        }}
        onRulesUpdated={() => {
          loadJobs()
        }}
      />
    </div>
  )
}

export default JobsTable
